import React, { memo } from "react";
import { SquarePen, History } from "lucide-react";
import { ConversationItem } from "~/components/assistant/ConversationItem";
import { IconButton } from "~/components/assistant/PopoverButton";
import { Conversation } from "~/components/assistant/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useConversations } from "~/contexts/conversations";
import { cn } from "~/lib/utils";

interface ChatSidebarProps {
  datasetTitle: string;
  conversations: Conversation[];
  currentConversationId: string | null;
  onNewChat: () => void;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, event: React.MouseEvent) => void;
  onRename: (conversationId: string, displayName: string) => void;
  onToggle?: () => void;
  isCollapsed?: boolean;
}

export const Sidebar = memo(function Sidebar({
  conversations,
  currentConversationId,
  onNewChat,
  onSelect,
  onDelete,
  onRename,
  onToggle,
  isCollapsed = false,
}: ChatSidebarProps) {
  const conversationsContext = useConversations();

  const formatConversationLabel = (conversation: Conversation) => {
    // Use display_name if available
    if (conversation.display_name) {
      return conversation.display_name;
    }

    const date = new Date(conversation.created_at);
    const now = new Date();

    const timeString = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    // Check if it's today, yesterday, or another date
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = (() => {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return date.toDateString() === yesterday.toDateString();
    })();

    let dateString: string;

    if (isToday) {
      dateString = "Today";
    } else if (isYesterday) {
      dateString = "Yesterday";
    } else {
      dateString = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }

    return `${dateString}, ${timeString}`;
  };

  const handleConversationSelect = (conversationId: string) => {
    onSelect(conversationId);
  };

  return (
    <div
      className={cn(
        "flex flex-col h-full",
        isCollapsed ? "w-auto" : "w-54 min-w-54",
        isCollapsed ? "" : "border-r border-gray-200"
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "p-3 flex items-center gap-2 ",
          isCollapsed ? "flex-col justify-center" : "justify-start"
        )}
      >
        {/* Collapse/Expand Button */}
        {onToggle && (
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <IconButton
                onClick={onToggle}
                variant={isCollapsed ? "ghost" : "secondary"}
                className={cn(!isCollapsed && "bg-gray-200 hover:bg-gray-200")}
              >
                <History className="size-4.5" />
              </IconButton>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isCollapsed ? "Show recents" : "Hide recents"}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* New Chat Button */}
        <Tooltip delayDuration={700}>
          <TooltipTrigger asChild>
            <IconButton onClick={onNewChat} variant="ghost">
              <SquarePen className="size-4.5" />
            </IconButton>
          </TooltipTrigger>
          <TooltipContent>
            <p>Start new analysis</p>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Chat History Section - only show when not collapsed */}
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto px-2 pb-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300">
          {/* Section Header */}
          <div className="px-2 py-1 mb-2">
            <h3 className="text-xs font-medium text-gray-500  tracking-wide">Recents</h3>
          </div>
          <div className="space-y-1">
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isSelected={conversation.id === currentConversationId}
                isStreaming={conversationsContext?.isStreaming(conversation.id) ?? false}
                showCheckmark={false}
                onSelect={handleConversationSelect}
                onDelete={onDelete}
                onRename={onRename}
                formatLabel={formatConversationLabel}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
